/**
 * HostingFileManager.tsx
 *
 * Phase 2 #3 — browser-based file manager for one hosting account.
 *
 * Layout:
 *   ┌─────────────┬───────────────────────────────────┐
 *   │ Tree (left) │  Editor / file actions (right)    │
 *   │             │                                    │
 *   │  /          │  toolbar: New folder, Upload,      │
 *   │   public_   │           Save, Download, Delete    │
 *   │     html    │                                    │
 *   │     ...     │  <textarea> file contents          │
 *   └─────────────┴───────────────────────────────────┘
 *
 * Paths shown to the user are RELATIVE to their home dir; "/" means
 * /home/<sftpuser>/.
 */

import React, { useEffect, useRef, useState } from 'react';

interface DirEntry {
  name: string;
  type: 'file' | 'dir' | 'link';
  size: number;
  mtime: number;
  perm: string;
}

interface Props {
  hostingAccountId: string;
}

const TEXT_EXT_RE = /\.(php|html?|css|js|jsx|ts|tsx|json|md|txt|xml|yaml|yml|sh|bash|conf|cfg|ini|env|htaccess|sql|csv|log)$/i;

const HostingFileManager: React.FC<Props> = ({ hostingAccountId }) => {
  const apiBase = process.env.REACT_APP_API_URL || '';
  const tokenRef = useRef<string | null>(localStorage.getItem('token'));

  const [path, setPath] = useState<string>('/');
  const [entries, setEntries] = useState<DirEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const [selectedFile, setSelectedFile] = useState<string | null>(null);
  const [editorContents, setEditorContents] = useState<string>('');
  const [editorOriginal, setEditorOriginal] = useState<string>('');
  const [editorLoading, setEditorLoading] = useState(false);
  const [editorSaving, setEditorSaving] = useState(false);
  const [editorSize, setEditorSize] = useState(0);

  const [busy, setBusy] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const authHeader = () => ({ 'Authorization': `Bearer ${tokenRef.current}` });

  const listPath = async (p: string) => {
    setLoading(true);
    setErr('');
    try {
      const resp = await fetch(`${apiBase}/api/hosting/accounts/${hostingAccountId}/files/list?path=${encodeURIComponent(p)}`, {
        headers: authHeader(),
      });
      const json = await resp.json();
      if (json.success) {
        setEntries(json.data.entries || []);
        setPath(json.data.path || '/');
      } else {
        setErr(json.error || 'Failed to list');
      }
    } catch (e: any) {
      setErr(e.message || 'Network error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { listPath('/'); /* eslint-disable-next-line */ }, [hostingAccountId]);

  const goUp = () => {
    if (path === '/' || !path) return;
    const parts = path.split('/').filter(Boolean);
    parts.pop();
    listPath('/' + parts.join('/'));
    setSelectedFile(null);
    setEditorContents('');
    setEditorOriginal('');
  };

  const enterDir = (name: string) => {
    const newPath = path === '/' ? `/${name}` : `${path}/${name}`;
    listPath(newPath);
    setSelectedFile(null);
    setEditorContents('');
    setEditorOriginal('');
  };

  const openFile = async (name: string) => {
    const filePath = path === '/' ? `/${name}` : `${path}/${name}`;
    setSelectedFile(filePath);
    setEditorContents('');
    setEditorOriginal('');
    if (!TEXT_EXT_RE.test(name)) return; // binary: only show download button
    setEditorLoading(true);
    setErr('');
    try {
      const resp = await fetch(`${apiBase}/api/hosting/accounts/${hostingAccountId}/files/read?path=${encodeURIComponent(filePath)}`, {
        headers: authHeader(),
      });
      const json = await resp.json();
      if (json.success) {
        setEditorContents(json.data.contents);
        setEditorOriginal(json.data.contents);
        setEditorSize(json.data.size);
      } else {
        setErr(json.error || 'Failed to read file');
      }
    } catch (e: any) {
      setErr(e.message || 'Network error');
    } finally {
      setEditorLoading(false);
    }
  };

  const save = async () => {
    if (!selectedFile) return;
    setEditorSaving(true);
    setErr('');
    try {
      const resp = await fetch(`${apiBase}/api/hosting/accounts/${hostingAccountId}/files/write`, {
        method: 'POST',
        headers: { ...authHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ path: selectedFile, contents: editorContents }),
      });
      const json = await resp.json();
      if (json.success) {
        setEditorOriginal(editorContents);
        await listPath(path);
      } else {
        setErr(json.error || 'Save failed');
      }
    } catch (e: any) {
      setErr(e.message || 'Network error');
    } finally {
      setEditorSaving(false);
    }
  };

  const downloadSelected = () => {
    if (!selectedFile) return;
    const url = `${apiBase}/api/hosting/accounts/${hostingAccountId}/files/download?path=${encodeURIComponent(selectedFile)}`;
    // Fetch with auth header, then trigger save-as via blob (browser <a download>
    // can't send auth headers natively).
    fetch(url, { headers: authHeader() }).then(async r => {
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        setErr(j.error || `Download failed (${r.status})`);
        return;
      }
      const blob = await r.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = selectedFile.split('/').pop() || 'file';
      a.click();
      URL.revokeObjectURL(a.href);
    });
  };

  const mkdir = async () => {
    const name = window.prompt('New folder name:');
    if (!name) return;
    if (/[\/\x00-\x1f]/.test(name)) { setErr('Invalid folder name'); return; }
    setBusy(true);
    setErr('');
    try {
      const newPath = path === '/' ? `/${name}` : `${path}/${name}`;
      const resp = await fetch(`${apiBase}/api/hosting/accounts/${hostingAccountId}/files/mkdir`, {
        method: 'POST',
        headers: { ...authHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ path: newPath }),
      });
      const json = await resp.json();
      if (json.success) await listPath(path);
      else setErr(json.error || 'mkdir failed');
    } catch (e: any) {
      setErr(e.message || 'Network error');
    } finally {
      setBusy(false);
    }
  };

  const renameEntry = async (entry: DirEntry) => {
    const newName = window.prompt(`Rename "${entry.name}" to:`, entry.name);
    if (!newName || newName === entry.name) return;
    if (/[\/\x00-\x1f]/.test(newName)) { setErr('Invalid name'); return; }
    setBusy(true);
    setErr('');
    try {
      const oldPath = path === '/' ? `/${entry.name}` : `${path}/${entry.name}`;
      const newPath = path === '/' ? `/${newName}` : `${path}/${newName}`;
      const resp = await fetch(`${apiBase}/api/hosting/accounts/${hostingAccountId}/files/rename`, {
        method: 'POST',
        headers: { ...authHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ oldPath, newPath }),
      });
      const json = await resp.json();
      if (json.success) {
        if (selectedFile === oldPath) {
          setSelectedFile(newPath);
        }
        await listPath(path);
      } else setErr(json.error || 'Rename failed');
    } catch (e: any) {
      setErr(e.message || 'Network error');
    } finally {
      setBusy(false);
    }
  };

  const deleteEntry = async (entry: DirEntry) => {
    if (!window.confirm(`Delete "${entry.name}"?${entry.type === 'dir' ? ' This will remove all files inside the folder.' : ''}\n\nThis cannot be undone.`)) return;
    setBusy(true);
    setErr('');
    try {
      const target = path === '/' ? `/${entry.name}` : `${path}/${entry.name}`;
      const resp = await fetch(`${apiBase}/api/hosting/accounts/${hostingAccountId}/files`, {
        method: 'DELETE',
        headers: { ...authHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ path: target }),
      });
      const json = await resp.json();
      if (json.success) {
        if (selectedFile === target) {
          setSelectedFile(null);
          setEditorContents('');
          setEditorOriginal('');
        }
        await listPath(path);
      } else setErr(json.error || 'Delete failed');
    } catch (e: any) {
      setErr(e.message || 'Network error');
    } finally {
      setBusy(false);
    }
  };

  const onUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setBusy(true);
    setErr('');
    try {
      const items: Array<{ name: string; contentBase64: string }> = [];
      for (let i = 0; i < files.length; i++) {
        const f = files[i];
        const b = await f.arrayBuffer();
        const b64 = arrayBufferToBase64(b);
        items.push({ name: f.name, contentBase64: b64 });
      }
      const resp = await fetch(`${apiBase}/api/hosting/accounts/${hostingAccountId}/files/upload`, {
        method: 'POST',
        headers: { ...authHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ path, files: items }),
      });
      const json = await resp.json();
      if (json.success) await listPath(path);
      else setErr(json.error || 'Upload failed');
    } catch (e: any) {
      setErr(e.message || 'Network error');
    } finally {
      setBusy(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const dirty = editorContents !== editorOriginal;
  const isTextFile = selectedFile && TEXT_EXT_RE.test(selectedFile);
  const breadcrumbs = path.split('/').filter(Boolean);

  return (
    <div className="mt-6 bg-white rounded-lg border border-gray-200 p-5">
      <div className="flex justify-between items-center mb-3">
        <div>
          <h2 className="font-semibold text-gray-900">File Manager</h2>
          <p className="text-xs text-gray-500 mt-0.5">Browse and edit files in your account's home directory.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={mkdir} disabled={busy} className="text-xs bg-gray-100 hover:bg-gray-200 px-2 py-1 rounded">New folder</button>
          <button onClick={() => fileInputRef.current?.click()} disabled={busy} className="text-xs bg-blue-600 text-white hover:bg-blue-700 px-2 py-1 rounded">Upload</button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={(e) => onUpload(e.target.files)}
          />
        </div>
      </div>

      {err && <div className="mb-3 bg-red-50 border border-red-200 rounded p-2 text-sm text-red-700">{err}</div>}

      {/* Breadcrumbs */}
      <div className="flex items-center gap-1 text-xs text-gray-600 mb-2 font-mono">
        <button onClick={() => listPath('/')} className="hover:underline">~</button>
        {breadcrumbs.map((part, idx) => {
          const sub = '/' + breadcrumbs.slice(0, idx + 1).join('/');
          return (
            <React.Fragment key={sub}>
              <span>/</span>
              <button onClick={() => listPath(sub)} className="hover:underline">{part}</button>
            </React.Fragment>
          );
        })}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
        {/* File tree (left) */}
        <div className="md:col-span-5 border border-gray-200 rounded">
          <div className="px-3 py-2 border-b bg-gray-50 flex items-center justify-between">
            <span className="text-xs text-gray-600">{entries.length} item(s)</span>
            {path !== '/' && (
              <button onClick={goUp} className="text-xs text-blue-600 hover:underline">↑ Up</button>
            )}
          </div>
          {loading ? (
            <div className="py-6 text-center"><div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-900 mx-auto" /></div>
          ) : entries.length === 0 ? (
            <div className="text-xs text-gray-500 py-3 text-center">Empty folder.</div>
          ) : (
            <div className="max-h-96 overflow-y-auto divide-y">
              {entries.map(e => (
                <div
                  key={e.name}
                  className={`group flex items-center justify-between px-3 py-1.5 text-sm hover:bg-blue-50 ${
                    selectedFile && selectedFile.endsWith(`/${e.name}`) ? 'bg-blue-50' : ''
                  }`}
                >
                  <button
                    onClick={() => e.type === 'dir' ? enterDir(e.name) : openFile(e.name)}
                    className="flex-1 text-left flex items-center gap-2 min-w-0"
                  >
                    <span className="text-base">{e.type === 'dir' ? '📁' : e.type === 'link' ? '🔗' : '📄'}</span>
                    <span className="font-mono text-xs truncate">{e.name}</span>
                  </button>
                  <span className="text-xs text-gray-400 ml-2 hidden sm:inline">
                    {e.type === 'file' ? formatSize(e.size) : ''}
                  </span>
                  <div className="opacity-0 group-hover:opacity-100 transition flex items-center gap-2 ml-3">
                    <button onClick={() => renameEntry(e)} className="text-xs text-blue-600 hover:underline">Rename</button>
                    <button onClick={() => deleteEntry(e)} className="text-xs text-red-600 hover:underline">Delete</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Editor / preview (right) */}
        <div className="md:col-span-7 border border-gray-200 rounded flex flex-col">
          {!selectedFile ? (
            <div className="p-6 text-center text-sm text-gray-500">
              Select a file from the tree to view or edit it.
            </div>
          ) : (
            <>
              <div className="px-3 py-2 border-b bg-gray-50 flex items-center justify-between">
                <span className="text-xs font-mono text-gray-700 truncate">{selectedFile}</span>
                <div className="flex items-center gap-2">
                  {isTextFile && (
                    <button
                      onClick={save}
                      disabled={editorSaving || !dirty}
                      className={`text-xs px-2 py-1 rounded ${dirty ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-gray-200 text-gray-500'} disabled:opacity-50`}
                    >
                      {editorSaving ? 'Saving…' : (dirty ? 'Save' : 'Saved')}
                    </button>
                  )}
                  <button onClick={downloadSelected} className="text-xs bg-gray-100 hover:bg-gray-200 px-2 py-1 rounded">Download</button>
                </div>
              </div>
              {editorLoading ? (
                <div className="py-6 text-center"><div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-900 mx-auto" /></div>
              ) : !isTextFile ? (
                <div className="p-6 text-sm text-gray-600">
                  This file isn't a text file we can edit in the browser. Use Download.
                </div>
              ) : (
                <textarea
                  className="flex-1 w-full font-mono text-xs p-3 outline-none resize-none min-h-[400px] border-0"
                  value={editorContents}
                  onChange={(e) => setEditorContents(e.target.value)}
                  spellCheck={false}
                />
              )}
              {isTextFile && editorSize > 0 && (
                <div className="px-3 py-1 border-t bg-gray-50 text-xs text-gray-500 flex justify-between">
                  <span>{formatSize(editorContents.length)}</span>
                  {dirty && <span className="text-amber-600">Unsaved changes</span>}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <p className="text-xs text-gray-500 mt-3">
        Browser editing is capped at 5 MB; downloads at 100 MB; uploads at 10 MB per request. For larger transfers use SFTP with the credentials in the Connection panel.
      </p>
    </div>
  );
};

function arrayBufferToBase64(buf: ArrayBuffer): string {
  let binary = '';
  const bytes = new Uint8Array(buf);
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + chunk)) as any);
  }
  return btoa(binary);
}

function formatSize(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

export default HostingFileManager;
